// src/pages/magasin/PreparateurDemande.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../Config';

export default function PreparateurDemande() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [demande, setDemande] = useState(null);
  const [quantites, setQuantites] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API_URL}/magasin/demandes/${id}`, { headers })
      .then(res => {
        const d = res.data.data;
        setDemande(d);
        const init = {};
        d.demande_materiels?.forEach(i => init[i.materiel_id] = i.quantite_preparee || 0);
        setQuantites(init);
      })
      .catch(() => setError('Demande non trouvée'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = (mid, val) => {
    const num = Math.max(0, parseInt(val) || 0);
    const item = demande?.demande_materiels?.find(i => i.materiel_id === mid);
    if (num > item.quantite_validee) {
      setError(`Quantité maximale autorisée : ${item.quantite_validee}`);
      return;
    }
    setError('');
    setQuantites(p => ({ ...p, [mid]: num }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const materiels = {};
      Object.entries(quantites).forEach(([id, qte]) => {
        if (qte > 0) materiels[id] = { quantite_preparee: qte };
      });

      await axios.post(`${API_URL}/magasin/demandes/${id}/preparer`, { materiels }, { headers });
      setDialogOpen(true);
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const totalQuantiteValidee = demande?.demande_materiels?.reduce((sum, item) => sum + (item.quantite_validee || 0), 0) || 0;
  const totalQuantitePreparee = Object.values(quantites).reduce((sum, qte) => sum + (qte || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de la demande...</p>
        </div>
      </div>
    );
  }

  if (!demande) {
    return (
      <div className="min-h-screen  p-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-700">Demande introuvable</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate('/magasin')}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Retour
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Préparation de la demande</h1>
              <p className="text-gray-600 mt-1">Référence : #{demande.numero || id}</p>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full">
                En préparation
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Informations générales */}
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations de la demande</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-sm text-gray-500 mb-1">Demandeur</div>
              <div className="font-medium text-gray-900">{demande.demandeur?.nom || 'Inconnu'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500 mb-1">Date de validation</div>
              <div className="font-medium text-gray-900">
                {new Date(demande.date_validation).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Résumé des quantités */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-2">Quantité validée</div>
            <div className="text-2xl font-bold text-blue-600">{totalQuantiteValidee}</div>
            <div className="text-sm text-gray-500 mt-1">Articles au total</div>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-2">Quantité préparée</div>
            <div className="text-2xl font-bold text-orange-600">{totalQuantitePreparee}</div>
            <div className="text-sm text-gray-500 mt-1">En cours</div>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-2">À préparer</div>
            <div className="text-2xl font-bold text-gray-900">{totalQuantiteValidee - totalQuantitePreparee}</div>
            <div className="text-sm text-gray-500 mt-1">Reste</div>
          </div>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Tableau des matériels */}
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Liste des matériels à préparer</h2>
            <p className="text-sm text-gray-500 mt-1">Saisissez les quantités préparées pour chaque matériel</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Matériel
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantité validée
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock disponible
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantité à préparer
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {demande.demande_materiels?.map((item) => {
                  const stock = item.materiel?.stock || 0;
                  const validee = item.quantite_validee || 0;
                  const preparee = quantites[item.materiel_id] || 0;
                  const stockInsuffisant = stock < validee;
                  
                  return (
                    <tr key={item.materiel_id} className="hover: transition-colors">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{item.materiel?.nom}</div>
                        {item.materiel?.reference && (
                          <div className="text-sm text-gray-500">Ref: {item.materiel.reference}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{validee}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className={`text-sm font-medium ${stockInsuffisant ? 'text-red-600' : 'text-gray-900'}`}>
                            {stock}
                          </div>
                          {stockInsuffisant && (
                            <div className="ml-2" title="Stock insuffisant">
                              <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <input
                            type="number"
                            min="0"
                            max={validee}
                            value={preparee}
                            onChange={(e) => handleChange(item.materiel_id, e.target.value)}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                          <div className="text-sm text-gray-500">
                            / {validee}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => navigate('/magasin')}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover: transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving || totalQuantitePreparee === 0}
            className={`px-3 py-1.5 rounded-lg font-medium transition-colors ${
              saving || totalQuantitePreparee === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {saving ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Enregistrement...
              </div>
            ) : (
              `Enregistrer (${totalQuantitePreparee} articles)`
            )}
          </button>
        </div>

        {/* Note */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="text-sm font-medium text-blue-800">Information</div>
              <div className="text-sm text-blue-700 mt-1">
                La quantité préparée ne peut pas dépasser la quantité validée. 
                Vérifiez les stocks disponibles avant de valider la préparation.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog de confirmation */}
      {dialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Préparation enregistrée
              </h3>
              <p className="text-gray-600 text-center mb-6">
                La demande a été mise à jour avec succès.
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => navigate('/magasin')}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retour au tableau de bord
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}